import React, { useEffect, useState, useMemo } from "react";
import { useForm, UseFormRegister } from "react-hook-form";
import { useT, Namespace, TranslationKey, useApiErrorMessage } from "@racoon/i18n";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Label,
  useToast,
  SearchableSelect,
} from "@racoon/ui";
import { ConnectProto, type ApiError } from "@racoon/api";
import { foreignOwnerName } from "~/shared/lib/holding-type";
import { useCanWriteType } from "~/shared/hooks/use-can-write-type";
import {
  useCreateTruckTypeMutation,
  useUpdateTruckTypeMutation,
} from "../../../../shared/api/truck/truck-type.api";
import { CapabilitiesMatrix } from "./capabilities-matrix.truck-type.admin";

type CustomTruckType = ConnectProto.CustomTruckType;

type TruckTypeFormData = Omit<
  ConnectProto.CustomTruckTypeCreate,
  "ownerOrganizationId" | "truckTypeCapabilitiesUpsert" | "$typeName"
>;

interface FormFieldProps {
  label: string;
  required?: boolean;
  register: UseFormRegister<TruckTypeFormData>;
  fieldName: any;
  validation?: Record<string, any>;
  placeholder: string;
  error?: string;
  type?: string;
  step?: string;
}

const FormField: React.FC<FormFieldProps> = ({
  label,
  register,
  fieldName,
  validation,
  placeholder,
  error,
  type = "text",
  step,
}) => (
  <div className="flex flex-row items-center gap-2">
    <Label className="font-medium text-text-primary text-sm flex flex-1 whitespace-nowrap">
      {label}
    </Label>
    <Input
      className="flex flex-1"
      type={type}
      step={step}
      placeholder={placeholder}
      {...register(fieldName, validation)}
    />
    {error && <p className="text-sm text-red-600 whitespace-nowrap">{error}</p>}
  </div>
);

interface TruckTypeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  truckType?: CustomTruckType | null;
  ownerOrganizationId?: number | null;
}

type DialogTab = "info" | "capabilities";

export const TruckTypeDialog: React.FC<TruckTypeDialogProps> = ({
  open,
  onOpenChange,
  truckType,
  ownerOrganizationId: ownerOrganizationIdProp,
}) => {
  const t = useT();
  const { toast } = useToast();
  const describeError = useApiErrorMessage();
  const isEdit = !!truckType;
  const ownerOrganizationId =
    ownerOrganizationIdProp ?? (truckType ? Number(truckType.ownerOrganizationId) : null);
  // Another company's type: shown, matrix included; changed only with a write right on its owner.
  const canWrite = useCanWriteType("truck", ownerOrganizationIdProp);
  const readOnly = !!truckType && !canWrite(truckType);
  const foreignOwner = truckType ? foreignOwnerName(truckType, ownerOrganizationId) : undefined;
  const [createTruckType, { isLoading: isCreating }] = useCreateTruckTypeMutation();
  const [updateTruckType, { isLoading: isUpdating }] = useUpdateTruckTypeMutation();
  const [activeTab, setActiveTab] = useState<DialogTab>("info");
  const isSubmitting = isCreating || isUpdating;

  const [selectedJobTypes, setSelectedJobTypes] = useState<ConnectProto.JobType[]>([]);
  const [selectedContainerTypeIds, setSelectedContainerTypeIds] = useState<number[]>([]);
  const [capabilitiesMatrix, setCapabilitiesMatrix] = useState<
    Record<number, Record<number, boolean>>
  >({});

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<TruckTypeFormData>({
    defaultValues: {
      manufacturer: "",
      model: "",
      name: "",
      type: 0 as ConnectProto.TruckType,
      loadType: 0 as ConnectProto.TruckLoadType,
      euroType: 0 as ConnectProto.TruckStandard,
      volume: 0,
      weightKg: 0,
      compactionFactor: 1,
    },
  });

  const formType = watch("type");
  const loadType = watch("loadType");
  const euroType = watch("euroType");

  // Enum → { id: String(enumValue), name: translated label } for the searchable selects
  const typeOptions = useMemo(
    () =>
      (Object.values(ConnectProto.TruckType).filter((v) => typeof v === "number") as number[])
        .filter((v) => v !== 0)
        .map((v) => ({
          id: String(v),
          name: t(Namespace.Enum, `TRUCK_TYPE_${ConnectProto.TruckType[v]}` as TranslationKey),
        })),
    [t],
  );

  const loadTypeOptions = useMemo(
    () =>
      (Object.values(ConnectProto.TruckLoadType).filter((v) => typeof v === "number") as number[])
        .filter((v) => v !== 0)
        .map((v) => ({
          id: String(v),
          name: t(
            Namespace.Enum,
            `TRUCK_LOAD_TYPE_${ConnectProto.TruckLoadType[v]}` as TranslationKey,
          ),
        })),
    [t],
  );

  const euroTypeOptions = useMemo(
    () =>
      (Object.values(ConnectProto.TruckStandard).filter((v) => typeof v === "number") as number[])
        .filter((v) => v !== 0)
        .map((v) => ({
          id: String(v),
          name: t(
            Namespace.Enum,
            `TRUCK_STANDARD_${ConnectProto.TruckStandard[v]}` as TranslationKey,
          ),
        })),
    [t],
  );

  useEffect(() => {
    if (open) {
      setActiveTab("info");
    }
  }, [open]);

  useEffect(() => {
    if (truckType) {
      reset({
        manufacturer: truckType.manufacturer,
        model: truckType.model,
        name: truckType.name || "",
        type: truckType.type as ConnectProto.TruckType,
        loadType: (truckType.loadType as ConnectProto.TruckLoadType) || 0,
        euroType: (truckType.euroType as ConnectProto.TruckStandard) || 0,
        volume: truckType.volume,
        weightKg: truckType.weightKg,
        compactionFactor: truckType.compactionFactor || 1,
      });

      const jobTypesSet = new Set<ConnectProto.JobType>();
      const containerTypeIdsSet = new Set<number>();
      const matrix: Record<number, Record<number, boolean>> = {};

      truckType.truckTypeCapabilities?.forEach((cap) => {
        const ctId = cap.containerType?.id;
        const jt = cap.jobType;
        if (ctId && jt) {
          jobTypesSet.add(jt);
          containerTypeIdsSet.add(Number(ctId));
          if (!matrix[Number(ctId)]) matrix[Number(ctId)] = {};
          matrix[Number(ctId)][jt] = true;
        }
      });

      setSelectedJobTypes(Array.from(jobTypesSet));
      setSelectedContainerTypeIds(Array.from(containerTypeIdsSet));
      setCapabilitiesMatrix(matrix);
    } else {
      reset({
        manufacturer: "",
        model: "",
        name: "",
        type: 0 as ConnectProto.TruckType,
        loadType: 0 as ConnectProto.TruckLoadType,
        euroType: 0 as ConnectProto.TruckStandard,
        volume: 0,
        weightKg: 0,
        compactionFactor: 1,
      });

      setSelectedJobTypes([]);
      setSelectedContainerTypeIds([]);
      setCapabilitiesMatrix({});
    }
  }, [truckType, reset]);

  const onSubmit = async (data: TruckTypeFormData) => {
    try {
      const submitCapabilities: ConnectProto.TruckCapabilityUpsert[] = [];
      selectedContainerTypeIds.forEach((ctId) => {
        selectedJobTypes.forEach((jt) => {
          if (capabilitiesMatrix[ctId]?.[jt]) {
            submitCapabilities.push({
              containerTypeId: BigInt(ctId),
              jobType: jt,
            } as any);
          }
        });
      });

      if (isEdit && truckType) {
        await updateTruckType({
          id: truckType.id,
          ownerOrganizationId: truckType.ownerOrganizationId,
          manufacturer: data.manufacturer,
          model: data.model,
          name: data.name,
          loadType: data.loadType || undefined,
          euroType: data.euroType || undefined,
          ...(data.type && { type: data.type }),
          volume: Number(data.volume),
          weightKg: data.weightKg ? Number(data.weightKg) : undefined,
          compactionFactor: data.compactionFactor ? Number(data.compactionFactor) : undefined,
          truckTypeCapabilities: submitCapabilities,
          toUpdateMatrix: true,
        }).unwrap();

        toast({
          title: t(Namespace.Settings, "truckType.dialog.updateSuccess"),
        });
      } else {
        if (!ownerOrganizationId) {
          toast({
            title: t(Namespace.Settings, "truckType.dialog.error"),
            description: t(Namespace.Settings, "truckType.dialog.companyRequired"),
            variant: "destructive",
          });
          return;
        }

        await createTruckType({
          ownerOrganizationId: BigInt(ownerOrganizationId),
          manufacturer: data.manufacturer,
          model: data.model,
          name: data.name,
          loadType: data.loadType || undefined,
          euroType: data.euroType || undefined,
          ...(data.type && { type: data.type }),
          volume: Number(data.volume),
          weightKg: data.weightKg ? Number(data.weightKg) : undefined,
          compactionFactor: data.compactionFactor ? Number(data.compactionFactor) : undefined,
          truckTypeCapabilities: submitCapabilities,
        }).unwrap();

        toast({
          title: t(Namespace.Settings, "truckType.dialog.createSuccess"),
        });
      }

      onOpenChange(false);
      reset();
    } catch (error) {
      toast({
        title: t(Namespace.Settings, "truckType.dialog.error"),
        description: describeError(error as ApiError),
        variant: "destructive",
      });
    }
  };

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent
        closeButtonTop="top-4"
        size="full"
        className="!w-[69.5rem] max-h-[80vh] max-w-[69.5rem] overflow-hidden flex flex-col"
      >
        <ModalHeader className="flex items-center !p-4 justify-between">
          <ModalTitle>
            {readOnly
              ? t(Namespace.Settings, "truckType.dialog.viewTitle")
              : isEdit
                ? t(Namespace.Settings, "truckType.dialog.editTitle")
                : t(Namespace.Settings, "truckType.dialog.createTitle")}
            {foreignOwner && (
              <span className="ml-2 text-sm font-normal text-text-secondary">{foreignOwner}</span>
            )}
          </ModalTitle>
        </ModalHeader>

        {/* Tabs */}
        <div className="px-4 pt-4">
          <div className="flex gap-1 bg-brand-blue-light p-1 rounded-xl">
            <button
              onClick={() => setActiveTab("info")}
              className={`flex-1 py-2 px-4 rounded-lg text-sm  transition-colors ${
                activeTab === "info" ? "bg-brand-blue text-white" : "text-text-primary"
              }`}
            >
              {t(Namespace.Settings, "truckType.dialog.infoTab")}
            </button>
            <button
              onClick={() => setActiveTab("capabilities")}
              className={`flex-1 py-2 px-4 rounded-lg text-sm  transition-colors ${
                activeTab === "capabilities" ? "bg-brand-blue text-white" : "text-text-primary"
              }`}
            >
              {t(Namespace.Settings, "truckType.dialog.capabilitiesTab")}
            </button>
          </div>
        </div>

        <ModalBody className="!p-0 !px-4 overflow-y-auto">
          {/* The form stays mounted regardless of the active tab so react-hook-form
              keeps its fields registered and validates them even when the user submits
              from the capabilities tab — otherwise a type with empty required fields
              could be created from there. */}
          <fieldset disabled={readOnly} className="contents">
            <form
              onSubmit={handleSubmit(onSubmit, () => setActiveTab("info"))}
              id="truck-type-form"
              className={activeTab === "info" ? "py-4" : "hidden"}
            >
              <div className="flex flex-1 gap-8">
                {/* Left column - Basic info */}
                <div className="flex flex-col flex-1 gap-4">
                  <FormField
                    label={t(Namespace.Settings, "truckType.dialog.name")}
                    register={register}
                    fieldName="name"
                    validation={{
                      required: t(Namespace.Settings, "truckType.dialog.nameRequired"),
                    }}
                    placeholder={t(Namespace.Settings, "truckType.dialog.namePlaceholder")}
                    error={errors.name?.message}
                  />
                  <FormField
                    label={t(Namespace.Settings, "truckType.dialog.manufacturer")}
                    register={register}
                    fieldName="manufacturer"
                    validation={{
                      required: t(Namespace.Settings, "truckType.dialog.manufacturerRequired"),
                    }}
                    placeholder={t(Namespace.Settings, "truckType.dialog.manufacturerPlaceholder")}
                    error={errors.manufacturer?.message}
                  />

                  <FormField
                    label={t(Namespace.Settings, "truckType.dialog.model")}
                    register={register}
                    fieldName="model"
                    validation={{
                      required: t(Namespace.Settings, "truckType.dialog.modelRequired"),
                    }}
                    placeholder={t(Namespace.Settings, "truckType.dialog.modelPlaceholder")}
                    error={errors.model?.message}
                  />
                  <div className="flex flex-row items-center gap-2">
                    <Label className="font-medium flex flex-1 whitespace-nowrap">
                      {t(Namespace.Settings, "truckType.dialog.category")}
                    </Label>
                    <SearchableSelect
                      id="truck-type-category"
                      value={formType ? String(formType) : ""}
                      options={typeOptions}
                      onChange={(id) => setValue("type", Number(id) as any)}
                      placeholder={t(Namespace.Settings, "truckType.dialog.categoryPlaceholder")}
                      searchPlaceholder={t(
                        Namespace.Settings,
                        "truckType.dialog.categoryPlaceholder",
                      )}
                      triggerExtraClassName="flex-1"
                    />
                    {errors.type && (
                      <p className="text-sm text-red-600 whitespace-nowrap">
                        {(errors.type as any)?.message}
                      </p>
                    )}
                  </div>
                </div>

                {/* Right column - Characteristics */}
                <div className="flex flex-1 flex-col gap-4">
                  <h3 className="font-semibold text-gray-900">
                    {t(Namespace.Settings, "truckType.dialog.characteristics")}
                  </h3>
                  <FormField
                    label={t(Namespace.Settings, "truckType.dialog.weight")}
                    register={register}
                    fieldName="weightKg"
                    type="number"
                    step="any"
                    validation={{
                      valueAsNumber: true,
                      min: {
                        value: 0.01,
                        message: t(Namespace.Settings, "truckType.dialog.weightMinError"),
                      },
                    }}
                    placeholder={t(Namespace.Settings, "truckType.dialog.weightPlaceholder")}
                    error={errors.weightKg?.message}
                  />
                  <FormField
                    label={t(Namespace.Settings, "truckType.dialog.volume")}
                    required
                    register={register}
                    fieldName="volume"
                    type="number"
                    step="any"
                    validation={{
                      required: t(Namespace.Settings, "truckType.dialog.volumeRequired"),
                      valueAsNumber: true,
                      min: {
                        value: 0.01,
                        message: t(Namespace.Settings, "truckType.dialog.volumeMinError"),
                      },
                    }}
                    placeholder={t(Namespace.Settings, "truckType.dialog.volumePlaceholder")}
                    error={errors.volume?.message}
                  />
                  <FormField
                    label={t(Namespace.Settings, "truckType.dialog.compactionFactor")}
                    register={register}
                    fieldName="compactionFactor"
                    type="number"
                    step="0.1"
                    validation={{
                      valueAsNumber: true,
                      min: {
                        value: 0.1,
                        message: t(Namespace.Settings, "truckType.dialog.compactionFactorMinError"),
                      },
                    }}
                    placeholder={t(
                      Namespace.Settings,
                      "truckType.dialog.compactionFactorPlaceholder",
                    )}
                    error={errors.compactionFactor?.message}
                  />
                  <div className="flex flex-row items-center gap-2">
                    <Label className="font-medium flex flex-1 whitespace-nowrap">
                      {t(Namespace.Settings, "truckType.dialog.loadType")}
                    </Label>
                    <SearchableSelect
                      id="truck-type-load-type"
                      value={loadType ? String(loadType) : ""}
                      options={loadTypeOptions}
                      onChange={(id) => setValue("loadType", Number(id) as any)}
                      placeholder={t(Namespace.Settings, "truckType.dialog.loadTypePlaceholder")}
                      searchPlaceholder={t(
                        Namespace.Settings,
                        "truckType.dialog.loadTypePlaceholder",
                      )}
                      triggerExtraClassName="flex-1"
                    />
                    {errors.loadType && (
                      <p className="text-sm text-red-600 whitespace-nowrap">
                        {(errors.loadType as any)?.message}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-row items-center gap-2">
                    <Label className="font-medium flex flex-1 whitespace-nowrap">
                      {t(Namespace.Settings, "truckType.dialog.euroType")}
                    </Label>
                    <SearchableSelect
                      id="truck-type-euro-type"
                      value={euroType ? String(euroType) : ""}
                      options={euroTypeOptions}
                      onChange={(id) => setValue("euroType", Number(id) as any)}
                      placeholder={t(Namespace.Settings, "truckType.dialog.euroTypePlaceholder")}
                      searchPlaceholder={t(
                        Namespace.Settings,
                        "truckType.dialog.euroTypePlaceholder",
                      )}
                      triggerExtraClassName="flex-1"
                    />
                    {errors.euroType && (
                      <p className="text-sm text-red-600 whitespace-nowrap">
                        {(errors.euroType as any)?.message}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </form>
          </fieldset>

          {activeTab === "capabilities" && (
            <fieldset disabled={readOnly} className="min-w-0 py-4">
              <CapabilitiesMatrix
                ownerOrganizationId={ownerOrganizationId}
                selectedJobTypes={selectedJobTypes}
                setSelectedJobTypes={setSelectedJobTypes}
                selectedContainerTypeIds={selectedContainerTypeIds}
                setSelectedContainerTypeIds={setSelectedContainerTypeIds}
                capabilitiesMatrix={capabilitiesMatrix}
                setCapabilitiesMatrix={setCapabilitiesMatrix}
              />
            </fieldset>
          )}
        </ModalBody>

        <ModalFooter className="p-4 mt-auto">
          {!readOnly && (
            <Button
              size="sm"
              onClick={() => handleSubmit(onSubmit, () => setActiveTab("info"))()}
              variant="opacityBlueWithOutline"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? t(Namespace.Buttons, "saving")
                : isEdit
                  ? t(Namespace.Settings, "truckType.dialog.saveButton")
                  : t(Namespace.Settings, "truckType.dialog.createButton")}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
